import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Table, Spinner } from 'react-bootstrap';
import { PrintLabelService } from '../../service/api/print-label-service';
import { InventoryService } from '../../service/api/inventory-service';
import { ReceivingShipmentService } from '../../service/api/receiving-shipment-service';
import { useDispatch } from 'react-redux';
import { showToast } from 'Core/redux/toast/slice';
import PropTypes from 'prop-types';

const DEFAULT_LOT_NUMBERS = [{ lotNumber: '', quantity: 1 }];

function resolveLotNumberForPrint(lot) {
  const trimmed = String(lot?.lotNumber ?? '').trim();
  if (trimmed) return trimmed;
  return `LOT${Math.random().toString(36).substr(2, 5).toUpperCase()}`;
}

// PartId 0 is valid (misc PO lines); only null/undefined/blank should be treated as missing.
const resolvePartIdForPrint = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const ticketField = (ticket, camel, pascal) => ticket?.[camel] ?? ticket?.[pascal];

const ticketHasInventory = (ticket) => {
  const inventoryId = ticketField(ticket, 'inventoryId', 'InventoryId');
  return inventoryId != null && Number(inventoryId) > 0;
};

const mergeAndSortPickTickets = (ticketLists) => {
  const byId = new Map();
  for (const ticket of ticketLists.flat()) {
    if (!ticket) continue;
    const id = ticketField(ticket, 'receivingPickTicketID', 'ReceivingPickTicketID');
    const key = id != null
      ? String(id)
      : `${ticketField(ticket, 'palletSequence', 'PalletSequence')}-${ticketField(ticket, 'lotNumber', 'LotNumber')}`;
    if (!byId.has(key)) {
      byId.set(key, ticket);
    }
  }
  return Array.from(byId.values()).sort((a, b) => {
    const lineCmp = String(ticketField(a, 'lineNumber', 'LineNumber') ?? '')
      .localeCompare(String(ticketField(b, 'lineNumber', 'LineNumber') ?? ''), undefined, { numeric: true });
    if (lineCmp !== 0) return lineCmp;
    return (Number(ticketField(a, 'palletSequence', 'PalletSequence')) || 0)
      - (Number(ticketField(b, 'palletSequence', 'PalletSequence')) || 0);
  });
};

const mapTicketToLotRow = (ticket) => ({
  lotNumber: ticketField(ticket, 'lotNumber', 'LotNumber')
    || ticketField(ticket, 'supplierLotNumber', 'SupplierLotNumber')
    || '',
  quantity: Number(ticketField(ticket, 'qtyPerBox', 'QtyPerBox')) || 1,
  receivingPickTicketId: ticketField(ticket, 'receivingPickTicketID', 'ReceivingPickTicketID'),
  inventoryId: ticketField(ticket, 'inventoryId', 'InventoryId') ?? null,
  serialNumber: ticketField(ticket, 'serialNumber', 'SerialNumber') ?? null,
  status: ticketField(ticket, 'status', 'Status') || 'Pending',
  selected: false,
});

const rowIsSelectable = (row) => {
  const status = String(row?.status || '').trim().toLowerCase();
  if (status === 'printed') return true;
  return status === 'queued' && Number(row?.inventoryId) > 0;
};

const modalStyle = {
  width: '100%',         // Modal scales with parent/dialog
  maxWidth: '1200px',    // Increased width to accommodate larger quantities
  height: 'auto',
  maxHeight: '90vh',     // Never exceeds 90% of viewport height
  paddingBottom: '2rem',
  overflow: 'auto',      // Scroll inside modal if needed
};

const tableScrollStyle = {
  width: '100%',
  overflowX: 'auto',
  overflowY: 'auto',
  maxHeight: '40vh',     // Optional: limit table height for usability
};

const MappingsPrintModal = ({
  show,
  onClose,
  onSubmit,
  poNumber = '',
  partNumber = '',
  quantity = '',
  receivingShipmentId = '',
  poReceivingId = '',
  partId = null,
  poItemDetailId = '',
  releaseIdExt = '',
  supplierName = '',
  userId = '',
  initialPrinter = '',
  initialNumberOfLabels = '',
  initialLotNumbers = DEFAULT_LOT_NUMBERS,
}) => {

  const [printer, setPrinter] = useState(initialPrinter);
  const [numberOfLabels, setNumberOfLabels] = useState(initialNumberOfLabels);
  const [lotNumbers, setLotNumbers] = useState(initialLotNumbers);
  const [printers, setPrinters] = useState([]);
  const [printersLoading, setPrintersLoading] = useState(false);
  const [printersError, setPrintersError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [lotNumbersAlreadySaved, setLotNumbersAlreadySaved] = useState(false);
  const [usingImportedPickTickets, setUsingImportedPickTickets] = useState(false);
  const [isReprintMode, setIsReprintMode] = useState(false);
  const [isRetryPrintMode, setIsRetryPrintMode] = useState(false);
  const [pickTicketsLoading, setPickTicketsLoading] = useState(false);
  const [copyLotDown, setCopyLotDown] = useState(false);
  const prevShowRef = useRef(false);
  const selectAllRef = useRef(null);
  const dispatch = useDispatch();

  // Helper to recalculate lot quantities
  const recalculateLotQuantities = (receivedQty, numLabels) => {
    const baseQty = Math.floor(receivedQty / numLabels);
    const remainder = receivedQty % numLabels;
    return Array.from({ length: numLabels }, (_, i) => ({
      lotNumber: '',
      quantity: i === 0 ? baseQty + remainder : baseQty,
    }));
  };

  useEffect(() => {
    if (show && !prevShowRef.current) {
      setPrinter(initialPrinter);
      setNumberOfLabels(initialNumberOfLabels);
      setLotNumbers(initialLotNumbers);
      setUsingImportedPickTickets(false);
      setLotNumbersAlreadySaved(false);
      setIsReprintMode(false);
      setIsRetryPrintMode(false);
      setCopyLotDown(false);
      setPrintersLoading(true);
      setPrintersError('');
      setSubmitError('');
      PrintLabelService.getPrinters()
        .then(res => {
          if (res && res.status === 200 && Array.isArray(res.response)) {
            setPrinters(res.response);
          } else {
            setPrinters([]);
            setPrintersError('Failed to load printers');
          }
        })
        .catch(() => {
          setPrinters([]);
          setPrintersError('Failed to load printers');
        })
        .finally(() => setPrintersLoading(false));

      if (receivingShipmentId && poItemDetailId) {
        setPickTicketsLoading(true);
        const basePickTicketQuery = {
          receivingShipmentID: Number(receivingShipmentId),
          poItemDetailID: Number(poItemDetailId),
          poReceivingID: poReceivingId ? Number(poReceivingId) : null,
        };
        if (releaseIdExt) {
          basePickTicketQuery.releaseIDExt = Number(releaseIdExt);
        }

        const loadPickTickets = async (status) => {
          const res = await ReceivingShipmentService.getPickTickets({ ...basePickTicketQuery, status });
          if (res?.status === 200 && Array.isArray(res.response) && res.response.length > 0) {
            return res.response;
          }
          return [];
        };

        (async () => {
          try {
            const [pendingTickets, queuedTickets, printedTickets] = await Promise.all([
              loadPickTickets('Pending'),
              loadPickTickets('Queued'),
              loadPickTickets('Printed'),
            ]);

            // Pending (no inventory yet) and Queued (inventory exists, printer may have failed)
            // must use PrintLabel — reprint API requires Status = Printed.
            const openTickets = mergeAndSortPickTickets([pendingTickets, queuedTickets]);
            if (openTickets.length > 0) {
              const importedLots = openTickets.map(mapTicketToLotRow);
              const hasQueuedInventory = openTickets.some(ticketHasInventory);
              setLotNumbers(importedLots);
              setNumberOfLabels(String(importedLots.length));
              setUsingImportedPickTickets(true);
              setLotNumbersAlreadySaved(false);
              setIsReprintMode(false);
              setIsRetryPrintMode(hasQueuedInventory);
              return;
            }

            if (printedTickets.length > 0) {
              const printedLots = printedTickets.map(mapTicketToLotRow);
              setLotNumbers(printedLots);
              setNumberOfLabels(String(printedLots.length));
              setUsingImportedPickTickets(false);
              setLotNumbersAlreadySaved(true);
              setIsReprintMode(true);
              setIsRetryPrintMode(false);
              return;
            }

            setLotNumbers(initialLotNumbers);
            setUsingImportedPickTickets(false);
            setLotNumbersAlreadySaved(false);
            setIsReprintMode(false);
            setIsRetryPrintMode(false);
          } catch {
            setUsingImportedPickTickets(false);
            setLotNumbersAlreadySaved(false);
            setIsReprintMode(false);
            setIsRetryPrintMode(false);
          } finally {
            setPickTicketsLoading(false);
          }
        })();
      }
    }
    prevShowRef.current = show;
  }, [show, initialPrinter, initialNumberOfLabels, initialLotNumbers, receivingShipmentId, poItemDetailId, poReceivingId, releaseIdExt]);

  useEffect(() => {
    if (show && quantity && numberOfLabels && !usingImportedPickTickets && !pickTicketsLoading && !isReprintMode && !isRetryPrintMode) {
      setLotNumbers(recalculateLotQuantities(Number(quantity), Number(numberOfLabels)));
    }
  }, [show, quantity, numberOfLabels, usingImportedPickTickets, pickTicketsLoading, isReprintMode, isRetryPrintMode]);

  const handleLotNumberChange = (idx, value) => {
    setLotNumbers(lots => lots.map((row, i) => {
      if (i === idx || (copyLotDown && idx === 0 && i > 0)) {
        return { ...row, lotNumber: value };
      }
      return row;
    }));
  };

  const handleCopyLotDown = (checked) => {
    setCopyLotDown(checked);
    if (!checked) return;
    setLotNumbers(lots => {
      const value = lots[0]?.lotNumber ?? '';
      return lots.map((row, i) => (i === 0 ? row : { ...row, lotNumber: value }));
    });
  };

  const handleLotQuantityChange = (idx, value) => {
    if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;
    setLotNumbers(lots => lots.map((row, i) => i === idx ? { ...row, quantity: value } : row));
  };

  const handleToggleReprintRow = (idx, checked) => {
    setLotNumbers(lots => lots.map((row, i) => (i === idx ? { ...row, selected: checked } : row)));
  };

  const handleToggleAllReprint = (checked) => {
    setLotNumbers(lots => lots.map((row) => (rowIsSelectable(row) ? { ...row, selected: checked } : row)));
  };

  const handleSubmit = async () => {
    if (!receivingShipmentId || !poReceivingId) {
      setSubmitError('Missing required information for print request');
      return;
    }

    const resolvedPartId = resolvePartIdForPrint(partId);
    if (resolvedPartId === null) {
      setSubmitError('Missing part ID for print request');
      return;
    }

    const rowsToSubmit = lotNumbers.filter((row) => !rowIsSelectable(row) || row.selected);
    if ((isReprintMode || isRetryPrintMode) && rowsToSubmit.length === 0) {
      setSubmitError('Select at least one label.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      const poNumberTrim = String(poNumber ?? '').trim();
      const supplierNameTrim = String(supplierName ?? '').trim();

      const pendingTicketRows = !isReprintMode && !isRetryPrintMode
        ? lotNumbers.filter((row) => Number(row.receivingPickTicketId) > 0 && !(Number(row.inventoryId) > 0))
        : [];
      if (pendingTicketRows.length > 0) {
        const qtyResponse = await ReceivingShipmentService.updatePendingPickTicketQuantities({
          receivingShipmentID: Number(receivingShipmentId),
          poItemDetailID: Number(poItemDetailId),
          releaseIDExt: releaseIdExt ? Number(releaseIdExt) : null,
          expectedQty: Number(quantity),
          items: pendingTicketRows.map((row) => ({
            receivingPickTicketId: Number(row.receivingPickTicketId),
            quantity: Number(row.quantity),
          })),
        });
        if (!qtyResponse || qtyResponse.status !== 200) {
          setSubmitError(qtyResponse?.errorMessage?.message || 'Label quantities must add up to the received quantity.');
          return;
        }
      }

      const printReceivingLabelRequest = {
        PartId: resolvedPartId,
        ReceivingShipmentId: Number(receivingShipmentId),
        POReceivingId: Number(poReceivingId),
        POItemDetailId: Number(poItemDetailId),
        Quantity: Number(quantity),
        UserId: userId,
        PONumber: poNumberTrim,
        PartNumber: partNumber || null,
        ReleaseIDExt: releaseIdExt ? Number(releaseIdExt) : null,
        IsReprint: isReprintMode,
        Status: 'Received',
        numberOfLabels: rowsToSubmit.length,
        PrinterId: printer,
        SupplierName: supplierNameTrim,
        Provider: 'Purchase',
        LotNumbers: rowsToSubmit.map(row => ({
          LotNumber: resolveLotNumberForPrint(row),
          Quantity: Number(row.quantity),
          ReceivingPickTicketId: row.receivingPickTicketId || null,
          InventoryId: row.inventoryId || null,
          SerialNumber: row.serialNumber || null,
        })),
      };

      const response = await InventoryService.printReceivingLabel(printReceivingLabelRequest);

      if (!response || response.status !== 200) {
        setSubmitError(response?.errorMessage?.message || 'Failed to print labels');
        return;
      }

      if (onSubmit) {
        onSubmit({
          printer,
          numberOfLabels: rowsToSubmit.length,
          lotNumbers: rowsToSubmit,
          printRequestId: response?.response?.InventoryId || response?.response?.inventoryId || null,
          isReprint: isReprintMode,
          lotNumberMessage: isReprintMode
            ? 'Labels reprinted successfully!'
            : isRetryPrintMode
              ? 'Print retry queued. Existing inventory will be reused.'
              : 'Labels printed successfully!',
        });
      }

      let successMessage = isReprintMode
        ? 'Labels reprinted successfully!'
        : isRetryPrintMode
          ? 'Print retry queued. Existing inventory will be reused.'
          : 'Labels printed successfully!';
      if (response?.response?.message || response?.response?.Message) {
        successMessage = response.response.message || response.response.Message;
      }

      dispatch(showToast({
        message: successMessage,
        type: 'success',
      }));

      if (onClose) onClose();
    } catch (error) {
      if (error.message === 'Request timed out') {
        setSubmitError('Request timed out. The print operation is taking longer than expected. Please try again.');
      } else if (error.message && error.message.includes('Network error')) {
        setSubmitError('Network error occurred. Please check your connection and try again.');
      } else {
        setSubmitError(error.message && error.message !== 'An error occurred' && error.message !== 'An unexpected error occurred'
          ? error.message
          : 'An error occurred while printing the labels');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const quantityLocked = isReprintMode || isRetryPrintMode || lotNumbersAlreadySaved;
  const lotLocked = isReprintMode || isRetryPrintMode || lotNumbersAlreadySaved;
  const allLotsHaveInventory = lotNumbers.length > 0
    && lotNumbers.every((row) => row.inventoryId != null && Number(row.inventoryId) > 0);

  // Validation: sum of quantities must match received quantity (only when lots can be edited)
  const roundLabelQty = (value) => Math.round((Number(value) || 0) * 100) / 100;
  const totalLotQty = roundLabelQty(lotNumbers.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0));
  const receivedQty = roundLabelQty(quantity);
  const labelQtyMatches = totalLotQty === receivedQty;
  const selectableRows = lotNumbers.filter(rowIsSelectable);
  const selectedRows = selectableRows.filter((row) => row.selected);
  const pendingRows = lotNumbers.filter((row) => !rowIsSelectable(row));
  const showSelection = selectableRows.length > 0;
  const allSelectableChecked = showSelection && selectedRows.length === selectableRows.length;
  const actionCount = (isReprintMode || isRetryPrintMode)
    ? pendingRows.length + selectedRows.length
    : Number(numberOfLabels);
  const mustSelectLabel = showSelection && actionCount === 0;
  const isPrintDisabled = (!quantityLocked && !labelQtyMatches) || submitting || !printer || mustSelectLabel;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedRows.length > 0 && !allSelectableChecked;
    }
  }, [selectedRows.length, allSelectableChecked]);

  // Helper function to format lot numbers nicely
  const formatLotNumbers = () => {
    if (isReprintMode) return 'Reprint existing printed labels (no new inventory)';
    if (isRetryPrintMode && allLotsHaveInventory) {
      return 'Retry print for queued labels — existing inventory/serials will be reused';
    }
    if (isRetryPrintMode) {
      return 'Pending labels will create inventory once; queued labels will reuse existing inventory';
    }
    if (lotNumbersAlreadySaved) return 'Using saved lot numbers for this part';
    
    const validLots = lotNumbers.filter(lot => lot.lotNumber && lot.lotNumber.trim());
    if (validLots.length === 0) return 'No lot numbers specified';
    
    return validLots.map(lot => `${lot.lotNumber} (${lot.quantity})`).join(', ');
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
      dialogClassName="custom-mappings-print-modal"
      contentClassName="custom-mappings-print-modal-content"
      style={{}}
    >
      <div style={modalStyle}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isReprintMode ? 'Reprint Labels' : isRetryPrintMode ? 'Retry Print' : lotNumbersAlreadySaved ? 'Print Labels Again' : 'Print Labels'}
            {isReprintMode && (
              <small className="text-muted d-block mt-1">
                Select the labels to reprint. This uses the existing serial and does not create inventory.
              </small>
            )}
            {isRetryPrintMode && !isReprintMode && (
              <small className="text-muted d-block mt-1">
                {allLotsHaveInventory
                  ? 'Select the queued labels to retry. This reuses the existing inventory and does not create a new serial.'
                  : 'Pending labels are printed together. Check a queued label to retry it without creating a new serial.'}
              </small>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: 'calc(100% - 120px)' }}>
          <div className="row" style={{ height: '100%' }}>
            <div className="col-md-5">
              <div className="mb-1" style={{ fontSize: 15 }}><strong>PO Number:</strong> {poNumber || '-'}</div>
              <div className="mb-1" style={{ fontSize: 15 }}><strong>Part Number:</strong> {partNumber || '-'}</div>
              <div className="mb-1" style={{ fontSize: 15 }}><strong>Quantity:</strong> {quantity || '-'}</div>
              <Form.Group className="mb-2">
                <Form.Label>Printer</Form.Label>
                {printersLoading ? (
                  <div className="d-flex align-items-center"><Spinner animation="border" size="sm" className="me-2" /> Loading printers...</div>
                ) : printersError ? (
                  <div className="text-danger">{printersError}</div>
                ) : (
                  <Form.Select style={{ maxWidth: 250 }} value={printer} onChange={e => setPrinter(e.target.value)}>
                    <option value="">Select Printer</option>
                    {printers.map(p => (
                      <option value={p.printerId} key={p.printerId}>{p.printerName}</option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Number of Labels</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={numberOfLabels}
                  onChange={e => setNumberOfLabels(e.target.value)}
                  placeholder="Enter number of labels"
                  style={{ maxWidth: 200 }}
                  disabled={isReprintMode || isRetryPrintMode || usingImportedPickTickets || lotNumbersAlreadySaved}
                />
                {usingImportedPickTickets && !isReprintMode && !isRetryPrintMode && (
                  <small className="text-muted d-block mt-1">Label quantities start out equal. You can change them as long as they add up to the received quantity.</small>
                )}
              </Form.Group>
            </div>
            <div className="col-md-7 d-flex flex-column" style={{ height: '100%' }}>
              <div className="mb-2 d-flex justify-content-between align-items-center">
                <strong>Lot Numbers</strong>
                {pickTicketsLoading && (
                  <small className="text-muted">
                    <Spinner animation="border" size="sm" className="me-1" />
                    Loading pick tickets...
                  </small>
                )}
                {!pickTicketsLoading && isRetryPrintMode && (
                  <small className="text-warning">
                    <i className="fa fa-refresh me-1"></i>
                    {allLotsHaveInventory
                      ? 'Loaded from queued pick tickets (retry print, no new inventory)'
                      : 'Loaded from pending and queued pick tickets'}
                  </small>
                )}
                {!pickTicketsLoading && isReprintMode && (
                  <small className="text-info">
                    <i className="fa fa-print me-1"></i>
                    Select the printed labels to reprint
                  </small>
                )}
              </div>
              <div style={tableScrollStyle}>
                <Table bordered size="sm">
                  <thead>
                    <tr>
                      {showSelection && (
                        <th style={{ width: 46, minWidth: 46 }}>
                          <input
                            ref={selectAllRef}
                            type="checkbox"
                            checked={allSelectableChecked}
                            onChange={(e) => handleToggleAllReprint(e.target.checked)}
                            title="Select all labels that can be reprinted"
                            aria-label="Select all labels that can be reprinted"
                          />
                        </th>
                      )}
                      <th style={{ width: 60, minWidth: 50 }}>Sl No</th>
                      <th style={{ minWidth: 300, maxWidth: 350, width: '50%' }}>Lot Number</th>
                      <th style={{ width: 120, minWidth: 100 }}>Quantity</th>
                      {(usingImportedPickTickets || isReprintMode || isRetryPrintMode) && (
                        <th style={{ width: 90, minWidth: 80 }}>Status</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {lotNumbers.map((row, idx) => (
                      <tr key={row.receivingPickTicketId || idx}>
                        {showSelection && (
                          <td>
                            {rowIsSelectable(row) ? (
                              <input
                                type="checkbox"
                                checked={Boolean(row.selected)}
                                onChange={(e) => handleToggleReprintRow(idx, e.target.checked)}
                                aria-label={`Select label ${idx + 1}`}
                              />
                            ) : null}
                          </td>
                        )}
                        <td>{idx + 1}</td>
                        <td>
                          {idx === 0 && !lotLocked && lotNumbers.length > 1 ? (
                            <div className="d-flex align-items-center gap-2">
                              <Form.Control
                                type="text"
                                value={row.lotNumber}
                                onChange={e => handleLotNumberChange(idx, e.target.value)}
                                placeholder="Enter lot number"
                              />
                              <Form.Check
                                type="checkbox"
                                id="copy-lot-down"
                                label="Repeat"
                                checked={copyLotDown}
                                onChange={e => handleCopyLotDown(e.target.checked)}
                                className="mb-0 text-nowrap user-select-none"
                                style={{ fontSize: 13, color: '#5aa7e0' }}
                                title="Use this lot number on every label below"
                              />
                            </div>
                          ) : (
                            <Form.Control
                              type="text"
                              value={row.lotNumber}
                              onChange={e => handleLotNumberChange(idx, e.target.value)}
                              placeholder="Enter lot number"
                              disabled={lotLocked || copyLotDown}
                            />
                          )}
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            min={1}
                            value={row.quantity}
                            onChange={e => handleLotQuantityChange(idx, e.target.value)}
                            placeholder="Qty"
                            disabled={quantityLocked}
                            style={{ minWidth: '100px', width: '100%' }}
                          />
                        </td>
                        {(usingImportedPickTickets || isReprintMode || isRetryPrintMode) && (
                          <td>
                            <small>
                              {row.status || (isReprintMode ? 'Printed' : (row.inventoryId ? 'Queued' : 'Pending'))}
                            </small>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                                  </Table>
                  
                  {/* Lot Numbers Summary */}
                  <div className="mt-3 p-2 bg-light rounded">
                    <small className="text-muted">
                      <strong>Summary:</strong> {formatLotNumbers()}
                    </small>
                    {!quantityLocked && (
                      <div className="mt-1">
                        <small className={`${labelQtyMatches ? 'text-success' : 'text-danger'}`}>
                          <i className={`fa ${labelQtyMatches ? 'fa-check-circle' : 'fa-exclamation-circle'} me-1`}></i>
                          Total: {totalLotQty} / {quantity} {labelQtyMatches ? '(✓ Matches)' : '(✗ Mismatch)'}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          {submitError && (
            <div className="alert alert-danger mt-3" role="alert">
              {submitError}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ paddingBottom: '2rem' }}>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isPrintDisabled}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {isReprintMode ? 'Reprinting...' : isRetryPrintMode ? 'Retrying print...' : lotNumbersAlreadySaved ? 'Creating Print Request...' : 'Saving...'}
              </>
            ) : (
              isReprintMode
                ? `Reprint (${actionCount} labels)`
                : isRetryPrintMode
                  ? `Retry Print (${actionCount} labels)`
                  : lotNumbersAlreadySaved
                    ? `Print Again (${numberOfLabels} labels)`
                    : `Print ${numberOfLabels} Labels`
            )}
          </Button>
          {!submitting && isPrintDisabled && (
            <div className="text-danger ms-3">
              {!printer ? 'Please select a printer.' :
               mustSelectLabel ? 'Select at least one label.' :
               (!quantityLocked ? `Total quantity must match received quantity (${quantity}).` : '')}
            </div>
          )}
        </Modal.Footer>
      </div>
    </Modal>
  );
};

MappingsPrintModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  poNumber: PropTypes.string,
  partNumber: PropTypes.string,
  quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  receivingShipmentId: PropTypes.string,
  poReceivingId: PropTypes.string,
  partId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  poItemDetailId: PropTypes.string,
  releaseIdExt: PropTypes.string,
  supplierName: PropTypes.string,
  userId: PropTypes.string,
  initialPrinter: PropTypes.string,
  initialNumberOfLabels: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialLotNumbers: PropTypes.array,
};

export default MappingsPrintModal; 